import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface TablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
}

type PageItem = number | 'ellipsis';

/** 生成页码序列,超过 7 页时折叠为「首页 … 邻页 … 末页」。 */
function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [
      1,
      'ellipsis',
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }
  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
}

export function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  isLoading,
}: TablePaginationProps) {
  const [jumpPage, setJumpPage] = React.useState(String(page));
  const totalPages = Math.ceil(total / pageSize);
  const safeTotalPages = Math.max(1, totalPages);
  const pageItems = getPageItems(page, safeTotalPages);

  React.useEffect(() => {
    setJumpPage(String(page));
  }, [page]);

  const changePage = (next: number) => {
    const clamped = Math.min(Math.max(next, 1), safeTotalPages);
    if (clamped !== page) onPageChange(clamped);
  };

  const jumpToPage = () => {
    if (!jumpPage.trim()) return;
    const parsed = Number(jumpPage);
    if (!Number.isFinite(parsed)) return;
    changePage(Math.trunc(parsed));
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-2 py-2">
      <div className="flex flex-wrap items-center gap-3 text-[0.8125rem] text-muted-foreground">
        <span>共 {total} 条</span>
        {onPageSizeChange ? (
          <select
            aria-label="选择每页条数"
            value={String(pageSize)}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            disabled={isLoading}
            className="h-8 rounded-sm border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={String(size)}>
                {size}条/页
              </option>
            ))}
          </select>
        ) : (
          <span className="flex h-8 items-center rounded-sm border border-input bg-background px-3 text-sm text-foreground shadow-sm">
            {pageSize}条/页
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 rounded-sm p-0"
          onClick={() => changePage(page - 1)}
          disabled={page <= 1 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">上一页</span>
        </Button>
        {pageItems.map((item, index) =>
          item === 'ellipsis' ? (
            <div
              key={`ellipsis-${index}`}
              className="flex h-8 min-w-8 items-center justify-center rounded-sm bg-muted px-2 text-sm font-medium text-muted-foreground"
            >
              ...
            </div>
          ) : (
            <Button
              key={item}
              variant={item === page ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'h-8 min-w-8 rounded-sm px-2 text-sm font-medium',
                item !== page && 'bg-muted/60 shadow-none hover:bg-muted',
              )}
              onClick={() => changePage(item)}
              disabled={isLoading}
            >
              {item}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 rounded-sm p-0"
          onClick={() => changePage(page + 1)}
          disabled={page >= totalPages || isLoading}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">下一页</span>
        </Button>
        <div className="ml-4 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">前往</span>
          <Input
            type="number"
            min={1}
            max={safeTotalPages}
            inputMode="numeric"
            aria-label="输入页码"
            value={jumpPage}
            onChange={(event) => setJumpPage(event.target.value)}
            onBlur={jumpToPage}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
                jumpToPage();
              }
            }}
            disabled={isLoading}
            className="h-8 w-16 rounded-sm px-2 text-center text-sm"
          />
          <span className="text-sm text-muted-foreground">页</span>
        </div>
      </div>
    </div>
  );
}
